'use client';

import React, { useMemo } from 'react';
import { ChevronRight, Fuel, Receipt, Wrench, ShoppingBag, Truck, Banknote, PackageCheck, Undo2, FileMinus, Route } from 'lucide-react';
import type { ChildEntryType, FeatureFlags } from '../types';
import { getChildEntryFormRegistry } from '@/config/child-entry-form/configLoader';
import { resolveEntryTypeOption } from '@/config/child-entry-form/configResolver';

export interface ExpenseEntryOption {
  type: ChildEntryType;
  label: string;
  description: string;
  icon: React.ElementType;
  color: string;
  isJobLinked?: boolean;
  featureKey?: keyof FeatureFlags;
}

/** Hardcoded fallback (used when YAML registry is not initialized) */
export const EXPENSE_ENTRY_OPTIONS: ExpenseEntryOption[] = [
  { type: 'fuel', label: 'Fuel', description: 'Record fuel stops with odometer and receipt', icon: Fuel, color: '#3b82f6', featureKey: 'fuelRecording' },
  { type: 'toll', label: 'Toll', description: 'Capture toll fees and payment details', icon: Receipt, color: '#3b82f6', featureKey: 'tollRecording' },
  { type: 'maintenance', label: 'Vehicle Maintenance', description: 'Log maintenance work and costs', icon: Wrench, color: '#3b82f6', featureKey: 'maintenanceRecording' },
  { type: 'purchase', label: 'Job linked Expense', description: 'Record expenses linked to a specific job', icon: ShoppingBag, color: '#3b82f6', isJobLinked: true, featureKey: 'purchaseRecording' },
  { type: 'casual_wage', label: 'Casual Wages', description: 'Record cash paid to casual labourers linked to a job', icon: Banknote, color: '#f59e0b', isJobLinked: true },
  { type: 'delivery_note', label: 'Supplier Delivery Note', description: 'Capture supplier delivery notes', icon: Truck, color: '#10b981', featureKey: 'procurementDocumentCapture' },
  { type: 'supplier_invoice', label: 'Supplier Invoice', description: 'Record supplier invoices for payment', icon: Receipt, color: '#10b981', featureKey: 'procurementDocumentCapture' },
  { type: 'grn', label: 'Goods Received Note', description: 'Capture goods received notes (GRN)', icon: PackageCheck, color: '#10b981', featureKey: 'procurementDocumentCapture' },
  { type: 'purchase_return', label: 'Purchase Return', description: 'Record goods returned to supplier', icon: Undo2, color: '#ef4444', featureKey: 'procurementDocumentCapture' },
  { type: 'supplier_credit', label: 'Supplier Credit Note', description: 'Capture supplier credit notes', icon: FileMinus, color: '#ef4444', featureKey: 'procurementDocumentCapture' },
  { type: 'travel', label: 'Travel / Mileage', description: 'Submit business mileage for SARS-rate reimbursement', icon: Route, color: '#6366f1' },
  { type: 'purchase', label: 'Smart Expense Capture', description: 'Record general business expenses', icon: ShoppingBag, color: '#3b82f6', isJobLinked: false },
];

/**
 * Get the entry type options for the TypeSelectionScreen.
 * Tries the YAML registry (source of truth) first, falls back to EXPENSE_ENTRY_OPTIONS.
 */
export function getExpenseEntryOptions(): ExpenseEntryOption[] {
  const registry = getChildEntryFormRegistry();
  if (registry.isInitialized()) {
    const yamlOptions = registry.getEntryTypeOptions();
    if (yamlOptions.length > 0) {
      return yamlOptions.map(resolveEntryTypeOption);
    }
  }
  return EXPENSE_ENTRY_OPTIONS;
}

interface EntryTypeSelectorProps {
  onSelect: (type: ChildEntryType, isJobLinked?: boolean) => void;
  isDark?: boolean;
  className?: string;
  /** Entry types to hide from the selector (e.g. procurement types when simplified) */
  hiddenTypes?: ChildEntryType[];
  /** Feature flags for RBAC-based filtering of entry types */
  features?: FeatureFlags | null;
}

export default function EntryTypeSelector({
  onSelect,
  isDark = false,
  className = '',
  hiddenTypes = [],
  features = null,
}: EntryTypeSelectorProps) {
  const visibleOptions = useMemo(
    () => getExpenseEntryOptions().filter((option) => {
      if (hiddenTypes.includes(option.type)) return false;
      if (features && option.featureKey && !features[option.featureKey]) return false;
      return true;
    }),
    [hiddenTypes, features],
  );

  return (
    <div className={`px-2 rounded-lg border p-1 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} ${className}`}>
      {visibleOptions.map((option, index) => {
        const Icon = option.icon;
        return (
          <button
            key={option.label}
            onClick={() => onSelect(option.type, option.isJobLinked)}
            className={`${index > 0 ? 'mt-2' : ''} w-full p-3 rounded-md border border-gray-200/90 dark:border-gray-700/90 bg-white/95 dark:bg-gray-800/90 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group`}
          >
            <div className="flex items-start gap-3 text-left">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm shrink-0"
                style={{ backgroundColor: option.color }}
              >
                <Icon className="w-4.5 h-4.5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 dark:text-white text-sm">
                  {option.label}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 leading-snug">
                  {option.description}
                </div>
              </div>
              <ChevronRight className="mt-1 h-4 w-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors shrink-0" />
            </div>
          </button>
        );
      })}
    </div>
  );
}
